import React, { useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { Loading } from "go-core/components/Loading";
import { useInfiniteScroll } from "go-core/hooks/useInfiniteScroll";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { CategoryApi, ItemApi } from "../../../../../../../../../services/Api/Organization/types";
import { LiveOrdersBlockedProductFilter } from "../../../services/types";
import LiveOrdersBlockItemModal from "../Form/LiveOrdersBlockItemModal";
import LiveOrdersBlockedItemsFiltersModal from "./LiveOrdersBlockedItemsFiltersModal";
import LiveOrdersBlockedItemsTableModal from "./LiveOrdersBlockedItemsTableModal";

interface Props {
	isShown: boolean;
	onHide: () => void;
}

const LiveOrdersBlockedItemsModal = ({ isShown, onHide }: Props): JSX.Element => {
	const { t } = useTranslation();
	const [selectedFilter, setSelectedFilter] = useState<LiveOrdersBlockedProductFilter>(
		LiveOrdersBlockedProductFilter.PRODUCTS
	);
	const [search, setSearch] = useState<string>("");
	const [itemToBlock, setItemToBlock] = useState<ItemApi | CategoryApi | undefined>();
	const scrollRef = useRef(null);
	const [modalEditMode, setModalEditMode] = useState(false);
	const filtersForProducts = [
		LiveOrdersBlockedProductFilter.PRODUCTS,
		LiveOrdersBlockedProductFilter.BLOCKED_PRODUCTS,
	];
	const filtersForCategories = [
		LiveOrdersBlockedProductFilter.CATEGORIES,
		LiveOrdersBlockedProductFilter.BLOCKED_CATEGORIES,
	];
	const [blockedProductsAmount, setBlockedProductsAmount] = useState(0);
	const [blockedCategoriesAmount, setBlockedCategoriesAmount] = useState(0);
	const { addFlash } = useFlash();

	useEffect(() => {
		init();
	}, []);

	const getFilterForBlockedItems = (type: "all" | "blocked") => {
		if (type === "blocked") {
			return btoa(unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}suspended|e=true`)));
		}

		return "";
	};

	const getTypeForFilter = (filters: LiveOrdersBlockedProductFilter[]) => {
		if (filters.includes(selectedFilter) && selectedFilter.includes("BLOCKED")) return "blocked";
		return "all";
	};

	const fetchProducts = (params: Record<string, any>, options?: CancelTokenSource) => {
		const finalParams = {
			...params,
			include: "category,item_group",
			search,
			f: getFilterForBlockedItems(getTypeForFilter(filtersForProducts)),
		};
		return apiOrganization.getItems(finalParams, {
			cancelToken: options?.token,
		});
	};

	const fetchCategories = (params: Record<string, any>, options?: CancelTokenSource) => {
		const finalParams = {
			...params,
			search,
			f: getFilterForBlockedItems(getTypeForFilter(filtersForCategories)),
		};
		return apiOrganization.getCategories(finalParams, {
			cancelToken: options?.token,
		});
	};

	const fetchBlockedProductsAmount = async () => {
		try {
			const res = await apiOrganization.getItemsCount({
				f: getFilterForBlockedItems("blocked"),
			});
			setBlockedProductsAmount(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchBlockedCategoriesAmount = async () => {
		try {
			const res = await apiOrganization.getCategoriesCount({
				f: getFilterForBlockedItems("blocked"),
			});
			setBlockedCategoriesAmount(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const init = async () => {
		await fetchBlockedProductsAmount();
		await fetchBlockedCategoriesAmount();
	};

	const { items, loading, updateItem } = useInfiniteScroll({
		fetchItems: filtersForProducts.includes(selectedFilter) ? fetchProducts : fetchCategories,
		search,
		scrollContainerElementRef: scrollRef,
		fetchDependencies: [selectedFilter],
	});

	const handleSuccessItemAction = async (item: ItemApi | CategoryApi) => {
		updateItem(item);

		if ("category" in item) {
			await fetchBlockedProductsAmount();
		} else {
			await fetchBlockedCategoriesAmount();
		}
	};

	const getFilteredItems = () => {
		return items.filter((item: Record<string, any>) => {
			if (selectedFilter.includes("BLOCKED")) return item.suspended;
			return true;
		});
	};

	return (
		<>
			<Modal
				show={isShown}
				onHide={onHide}
				className={`live-orders-blocked-products-modal ${itemToBlock ? "d-none" : "unset"}`}
				size="lg"
			>
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.live_order.field.product_blocking.title")}</Modal.Title>
				</Modal.Header>
				<LiveOrdersBlockedItemsFiltersModal
					setSelectedFilter={setSelectedFilter}
					selectedFilter={selectedFilter}
					setSearch={setSearch}
					blockedCategoriesAmount={blockedCategoriesAmount}
					blockedProductsAmount={blockedProductsAmount}
				/>
				<Modal.Body ref={scrollRef}>
					<LiveOrdersBlockedItemsTableModal
						items={getFilteredItems()}
						setItemToBlock={setItemToBlock}
						loading={loading}
						handleUnblockItem={handleSuccessItemAction}
						setEditMode={setModalEditMode}
					/>
					{loading && <Loading />}
				</Modal.Body>
			</Modal>
			{itemToBlock && (
				<LiveOrdersBlockItemModal
					isShown={!!itemToBlock}
					onHide={() => setItemToBlock(undefined)}
					item={itemToBlock}
					handleSuccessAction={handleSuccessItemAction}
					setEditMode={setModalEditMode}
					editMode={modalEditMode}
				/>
			)}
		</>
	);
};

export default LiveOrdersBlockedItemsModal;
